import PropTypes from 'prop-types'
import styled from 'styled-components/macro'

import { IconTruckDrayage, IconTruckDryvan } from '@loadsmart/icons'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import Logo from 'legacy/assets/svg/logos/loadsmart.svg'
import CheckboxInstance from 'legacy/components/Checkbox/Checkbox'
import Alert from 'legacy/components/FormAlertV2/FormAlertV2'
import InputForm from 'legacy/components/Input/Input'
import ToggleButtonRound from 'legacy/components/ToggleButtonRound/ToggleButtonRound'
import { screen } from 'legacy/styles/screen'

import FormFooter from '../FormFooter'
import SignupSuccessPage from '../SignupSuccessPage/SignupSuccessPage'

const AlertWrapper = styled.div`
  margin: -3px 0 0 0;
`

const FormWrapper = styled.form`
  width: 548px;
  max-width: 300px;
  margin: auto;

  ${screen.md} {
    width: ${({ acceptLoad }) => (acceptLoad ? 'auto' : '80%')};
    max-width: 380px;
    max-height: ${({ acceptLoad }) => (acceptLoad ? 'fit-content' : '88vh')};
  }

  ${screen.lg} {
    max-width: ${({ acceptLoad }) => (acceptLoad ? '380px' : '450px')};
  }

  ${screen.xlg} {
    max-width: ${({ acceptLoad }) => (acceptLoad ? '380px' : '548px')};
  }
`

const Title = styled.h1`
  display: none;
  margin-bottom: 13px;

  color: ${getToken('color-neutral-white')};
  font-weight: bold;
  font-size: 40px;
  line-height: 52px;
  text-align: center;

  ${screen.md} {
    display: block;
  }
`

const CompanyText = styled.span`
  display: inline-block;
  margin-left: 11px;

  line-height: 44px;
  vertical-align: top;
`

const WrapperInput = styled.div`
  position: relative;

  margin-bottom: 20px;
`

const WrapperCheckbox = styled.div`
  position: relative;

  margin-bottom: 20px;
  padding: 20px 20px 4px 20px;

  background-color: ${getToken('color-neutral-dark')};
  border-radius: 5px;
`

const WrapperInputFirst = styled.div`
  position: relative;

  display: inline-block;
  width: 100%;
  margin-bottom: 20px;

  vertical-align: top;

  ${screen.md} {
    width: 48.5%;
  }
`

const WrapperInputLast = styled.div`
  position: relative;

  display: inline-block;
  width: 100%;
  margin-bottom: 20px;

  vertical-align: top;

  ${screen.md} {
    width: 47.5%;
    margin-left: 3.5%;
  }
`

const Input = styled(InputForm)`
  height: 35px;

  color: ${getToken('color-neutral-dark')};
  font-size: 16px;
  line-height: 21px;

  border: ${({ error }) =>
    error
      ? `2px solid ${getToken('color-warning-dark')}`
      : `1px solid ${getToken('color-neutral-lighter')}`};
`

const Label = styled.label`
  margin-bottom: 9px;
  display: block;
  color: ${getToken('color-neutral-white')};
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
`

const CheckBoxLabel = styled.label`
  position: relative;

  display: block;
  margin-bottom: 16px;
  padding: 0 0 0 25px;

  color: ${getToken('color-neutral-white')};
  font-size: 12px;
  line-height: 18px;

  input {
    position: absolute;
    top: 3px;
    left: -8px;
  }

  a {
    color: ${getToken('color-neutral-white')};
    text-decoration: underline;
  }
`

const Checkbox = styled(CheckboxInstance)`
  input {
    &:before {
      border: 2px solid ${getToken('color-neutral-white')};
    }
  }
  &:before {
    top: 4px;

    width: 16px;
    height: 10px;
  }
  &:after {
    top: 0;

    width: 24px;
    height: 24px;

    background-color: ${getToken('color-neutral-dark')};
    border: 2px solid ${getToken('color-neutral-lightest')};
    border-radius: 3px;
    box-shadow: none;
  }
`

const ToggleWrapper = styled.div`
  margin-top: 10px;
  margin-bottom: 20px;
`

const WrapperFormField = styled.div`
  label {
    text-transform: uppercase;
  }
  > div {
    vertical-align: bottom;
  }
`

function CarrierInnerForm({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  status,
  handleSubmit,
  isSubmitting,
  acceptLoad,
}) {
  return (
    <>
      {status && !status.submitted && (
        <FormWrapper name="carrierSignupForm" onSubmit={handleSubmit} acceptLoad={acceptLoad}>
          {!acceptLoad && (
            <>
              <Title>
                <img src={Logo} alt="Loadsmart" />
                <CompanyText>Sign up</CompanyText>
              </Title>
            </>
          )}
          <WrapperFormField>
            <WrapperInputFirst>
              <Label>First name</Label>
              <Input
                name="firstName"
                type="text"
                aria-required="true"
                placeholder="First name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.firstName}
                error={errors.firstName && touched.firstName}
              />
              {errors.firstName && touched.firstName && (
                <AlertWrapper>
                  <Alert message={errors.firstName} />
                </AlertWrapper>
              )}
            </WrapperInputFirst>
            <WrapperInputLast>
              <Label>Last name</Label>
              <Input
                name="lastName"
                type="text"
                aria-required="true"
                placeholder="Last name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.lastName}
                error={errors.lastName && touched.lastName}
              />
              {errors.lastName && touched.lastName && (
                <AlertWrapper>
                  <Alert message={errors.lastName} />
                </AlertWrapper>
              )}
            </WrapperInputLast>
            <WrapperInput>
              <Label>Email</Label>
              <Input
                name="email"
                type="email"
                aria-required="true"
                placeholder="Email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                error={errors.email && touched.email}
              />
              {errors.email && touched.email && (
                <AlertWrapper>
                  <Alert message={errors.email} />
                </AlertWrapper>
              )}
            </WrapperInput>
            <WrapperInputFirst>
              <Label>Password</Label>
              <Input
                name="password"
                type="password"
                aria-required="true"
                placeholder="Password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
                error={errors.password && touched.password}
              />
              {errors.password && touched.password && (
                <AlertWrapper>
                  <Alert message={errors.password} />
                </AlertWrapper>
              )}
            </WrapperInputFirst>
            <WrapperInputLast>
              <Label>Password confirmation</Label>
              <Input
                name="repeatPassword"
                type="password"
                aria-required="true"
                placeholder="Password confirmation"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.repeatPassword}
                error={errors.repeatPassword && touched.repeatPassword}
              />
              {errors.repeatPassword && touched.repeatPassword && (
                <AlertWrapper>
                  <Alert message={errors.repeatPassword} />
                </AlertWrapper>
              )}
            </WrapperInputLast>
            <WrapperInputFirst>
              <Label>DOT Number</Label>
              <Input
                name="dot_number"
                id="dot_number"
                type="number"
                aria-required="true"
                placeholder="DOT number"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.dot_number}
                error={errors.dot_number && touched.dot_number}
              />
              {errors.dot_number && touched.dot_number && (
                <AlertWrapper>
                  <Alert message={errors.dot_number} />
                </AlertWrapper>
              )}
            </WrapperInputFirst>
            <WrapperInputLast>
              <Label>MC Number</Label>
              <Input
                name="mc_number"
                id="mc_number"
                type="number"
                aria-required="true"
                placeholder="MC number"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.mc_number}
                error={errors.mc_number && touched.mc_number}
              />
              {errors.mc_number && touched.mc_number && (
                <AlertWrapper>
                  <Alert message={errors.mc_number} />
                </AlertWrapper>
              )}
            </WrapperInputLast>
          </WrapperFormField>
          <ToggleWrapper>
            <ToggleButtonRound
              options={[
                {
                  value: 'ftl',
                  label: 'Full Truck Load',
                  icon: () => {
                    return <IconTruckDryvan width="25px" height="25px" />
                  },
                },
                {
                  value: 'drayage',
                  label: 'Intermodal',
                  icon: () => {
                    return <IconTruckDrayage width="25px" height="25px" />
                  },
                },
              ]}
              name="equipmentType"
              value={values.equipmentType ? values.equipmentType : 'ftl'}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.equipmentType}
              error={errors.equipmentType && touched.equipmentType}
            />
            {errors.equipmentType && touched.equipmentType && (
              <AlertWrapper>
                <Alert message={errors.equipmentType} />
              </AlertWrapper>
            )}
          </ToggleWrapper>
          {values.equipmentType === 'drayage' && (
            <>
              <WrapperInput>
                <Label>SCAC CODE</Label>
                <Input
                  name="scac"
                  placeholder="SCAC code"
                  aria-required="true"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.scac}
                  error={errors.scac && touched.scac}
                />
                {errors.scac && touched.scac && (
                  <AlertWrapper>
                    <Alert message={errors.scac} />
                  </AlertWrapper>
                )}
              </WrapperInput>
              <WrapperCheckbox>
                <CheckBoxLabel>
                  <Checkbox
                    type="checkbox"
                    name="uiia"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.uiia}
                    error={errors.uiia && touched.uiia}
                  />
                  By checking this box, I confirm I have a{' '}
                  <a
                    href="https://www.uiia.org/motor-carriers-0"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    valid UIIA
                  </a>{' '}
                  for all equipment providers at the port terminals or rail ramps from where I will
                  accept Loadsmart shipments.
                  {errors.uiia && touched.uiia && <Alert message={errors.uiia} />}
                </CheckBoxLabel>
                <CheckBoxLabel>
                  <Checkbox
                    type="checkbox"
                    name="emodal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.emodal}
                    error={errors.emodal && touched.emodal}
                  />
                  By checking this box, I confirm I meet the requirements for access to any port
                  terminal or rail ramp from where I accept a Loadsmart shipment.
                  {errors.emodal && touched.emodal && <Alert message={errors.emodal} />}
                </CheckBoxLabel>
              </WrapperCheckbox>
            </>
          )}
          {errors.server && (
            <AlertWrapper>
              <Alert message={errors.server} />
            </AlertWrapper>
          )}
          <FormFooter isSubmitting={isSubmitting} showLogin={!acceptLoad} />
        </FormWrapper>
      )}
      {status && status.submitted && <SignupSuccessPage email={values.email} />}
    </>
  )
}

CarrierInnerForm.defaultProps = {
  errors: {},
  touched: {},
  status: {
    submitted: false,
  },
  handleChange: undefined,
  handleSubmit: undefined,
  isSubmitting: false,
}

CarrierInnerForm.propTypes = {
  status: PropTypes.shape({
    submitted: PropTypes.bool,
  }),
  errors: PropTypes.shape({}),
  touched: PropTypes.shape({}),
  handleChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
}

export default CarrierInnerForm
